import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import isBefore from 'date-fns/isBefore';
import endOfDay from 'date-fns/endOfDay';

import { convertToZurichTz, getDateZurich } from '@utils/date';

import { PRODUCT, SHOP_EVENT_PRODUCT } from '@common/routes.mjs';
import { PRODUCT_TYPE } from '@common/constants/products';
import { SectionTitle } from '../Content.styled';

import SizeLink from './SizeLink';
import { SizesContainer, Wrapper } from './Sizes.styled';

const Sizes = ({ sizes, articleNr, publishedUntil, slug, productType }) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const isShopEvent = productType === PRODUCT_TYPE.SHOP_EVENT;

  const isPublished = useMemo(
    () =>
      isBefore(
        getDateZurich(),
        convertToZurichTz(endOfDay(new Date(publishedUntil))),
      ),
    [publishedUntil],
  );

  return (
    <Wrapper>
      <SectionTitle>{t('productDetails.sizes', 'Grössen')}</SectionTitle>

      <SizesContainer>
        {sizes.map(({ sanitizedArticleNr, availability, title }) => (
          <SizeLink
            key={sanitizedArticleNr}
            active={articleNr === sanitizedArticleNr && !availability <= 0}
            articleNr={sanitizedArticleNr}
            disabled={availability <= 0 || (isShopEvent && !isPublished)}
            slug={slug}
            locale={language}
            route={isShopEvent ? SHOP_EVENT_PRODUCT : PRODUCT}
          >
            {title}
          </SizeLink>
        ))}
      </SizesContainer>
    </Wrapper>
  );
};

Sizes.propTypes = {
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      articleNr: PropTypes.string,
      availability: PropTypes.number,
      itemNr: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  articleNr: PropTypes.string,
  publishedUntil: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  productType: PropTypes.oneOf([PRODUCT_TYPE.DEFAULT, PRODUCT_TYPE.SHOP_EVENT])
    .isRequired,
};

Sizes.defaultProps = {
  sizes: [],
  articleNr: null,
};

export default Sizes;
